import {
  DESK_MIN_WIDTH,
  DESKTOP_VIEWPORT_WIDTHS,
  MAX_FRAME_RATIO_GAP,
  MAX_PIXEL_DENSITY,
  PregenerationSize,
  Size,
} from '@shared/components/image/models';

/**
 * Sizes the server's `srcset` offers for a frame declared `exact`, smallest first: the frame at 1x and
 * 2x, or each common desktop width at both densities for a fluid one. `fit` sizes an image that keeps
 * its ratio inside the frame.
 */
export const getServerCandidates = (frame: PregenerationSize | undefined, fit = (size: Size): Size => size): Size[] => {
  const height = frame?.height;
  if (!frame?.exact || !height) {
    return [];
  }

  const widths = frame.fluid ? DESKTOP_VIEWPORT_WIDTHS.filter((width) => width <= frame.width) : [frame.width];
  const sizes = widths.map((width) => fit({ width, height }));

  return [
    ...sizes,
    ...sizes.map((size) => ({ width: size.width * MAX_PIXEL_DENSITY, height: size.height * MAX_PIXEL_DENSITY })),
  ];
};

/**
 * Whether a file fills the box it is shown in, which is what object-cover needs: it upscales by the
 * larger of the two ratios. Covering in pixels is not enough: a file whose ratio differs from the box
 * gets cropped again from the centre, which drops the gravity the CMS set on the media (measured: the
 * homepage hero, 1920x1536 in a 390x744 frame).
 */
export const coversBox = (file: Size, box: Size): boolean => {
  const boxRatio = box.width / box.height;
  const fitsTheRatio = Math.abs(file.width / file.height - boxRatio) <= boxRatio * MAX_FRAME_RATIO_GAP;

  return fitsTheRatio && file.width >= box.width && file.height >= box.height;
};

/**
 * The candidate a browser takes from the server's `srcset` for this box, the smallest that covers it,
 * so the client asks for that very file instead of a second one. None where the frame is not the box.
 */
export const findServerCandidate = (
  frame: PregenerationSize | undefined,
  box: Size,
  fit?: (size: Size) => Size
): Size | undefined => {
  if (box.width <= 0 || box.height <= 0) {
    return undefined;
  }
  if (frame?.exact === 'desk' && !globalThis.matchMedia?.(`(min-width: ${DESK_MIN_WIDTH}px)`).matches) {
    return undefined;
  }

  return getServerCandidates(frame, fit).find((candidate) => coversBox(candidate, box));
};
